import { Controller } from "@hotwired/stimulus"

// Webpack emits hashed URLs under /packs — same files as SvgHelper#show_svg (app/javascript/images/…).
import microphoneUrl from "images/menu/microphone.svg"
import sportUrl from "images/menu/sport.svg"
import articlesUrl from "images/menu/news.svg"
import editUrl from "images/menu/edit.svg"
import starUrl from "images/menu/star.svg"
import textUrl from "images/menu/text.svg"
import commentsUrl from "images/menu/comments.svg"
import checklistUrl from "images/menu/checklist.svg"
import socialNetworkUrl from "images/menu/social-network.svg"
import formsUrl from "images/menu/forms.svg"

export default class extends Controller {
  static targets = ["list"]

  connect() {
    this.loaded = false
    this.badgeRequestId = 0
    this.refreshBadgeCount()
  }

  async open() {
    if (this.loaded) return
    this.loaded = true

    await this.load()
  }

  async load() {
    if (!this.hasListTarget) return

    try {
      const data = await this.fetchDropdownData()

      this.setBadgeCount(data.unseen_count || 0)
      this.renderList(data.notifications || [])
    } catch (e) {
      this.listTarget.innerHTML =
        '<div class="text-center text-danger small py-3">Unable to load notifications.</div>'
    }
  }

  async refreshBadgeCount() {
    const requestId = ++this.badgeRequestId

    try {
      const data = await this.fetchBadgeCount()
      if (requestId !== this.badgeRequestId) return
      this.applyBadgeCount(data.unseen_count || 0)
    } catch (e) {
      // Keep existing badge if preload fails.
    }
  }

  async fetchBadgeCount() {
    const resp = await fetch("/entity_admins/notifications/badge_count", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })

    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    return resp.json()
  }

  async fetchDropdownData() {
    const resp = await fetch("/entity_admins/notifications/dropdown", {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })

    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    return resp.json()
  }

  renderList(notifications) {
    if (!notifications.length) {
      this.listTarget.innerHTML =
        '<div class="text-center text-muted small py-3">No notifications.</div>'
      return
    }

    this.listTarget.innerHTML = notifications
      .map((n) => this.renderNotificationCard(n))
      .join("")
  }

  renderNotificationCard(n) {
    const icon = this.iconForCategory(n.category)
    const time = (n.relative_time || "").replace(/^about /, "")
    const newPill = n.new_pill_html || ""
    const card = `
      <div class="fw-notification-card fanword-border-radius p-3 mb-3">
        <div class="fw-notification-card__top mb-3">
          <div class="fw-notification-card__meta">
            <div class="fw-notification-card__icon fanword-border fanword-border-radius">
              ${icon}
            </div>
            ${newPill}
          </div>
          <span class="fw-notification-card__time color-fanword-black">${this.escapeHtml(time)}</span>
        </div>

        <div class="fw-notification-card__bottom">
          <span class="fw-notification-card__text color-fanword-black">${this.escapeHtml(n.text)}</span>
        </div>
      </div>
    `

    if (n.link) {
      return `<a href="${this.escapeAttr(n.link)}" class="text-decoration-none" data-turbo="false">${card}</a>`
    }

    return card
  }

  iconForCategory(category) {
    switch (category) {
      case "transcription":
        return this.svgImgTag(textUrl, "Transcription")
      case "feature_story":
        return this.svgImgTag(editUrl, "Feature Story")
      case "task":
        return this.svgImgTag(checklistUrl, "Task")
      case "online_interview":
        return this.svgImgTag(microphoneUrl, "Online Interview")
      case "support_ticket":
        return this.svgImgTag(commentsUrl, "Support Ticket")
      case "bio":
        return this.svgImgTag(sportUrl, "Player Bio")
      case "news_story":
        return this.svgImgTag(articlesUrl, "News Story")
      case "nomination":
        return this.svgImgTag(starUrl, "Nomination")
      case "team":
        return this.svgImgTag(socialNetworkUrl, "Team")
      case "form":
        return this.svgImgTag(formsUrl, "Form")
      default:
        return "<i class='fa fa-bell'></i>"
    }
  }

  setBadgeCount(count) {
    this.badgeRequestId++
    this.applyBadgeCount(count)
  }

  applyBadgeCount(count) {
    const link = this.element.querySelector(".fw-icon-badge__link")
    if (!link) return

    let badge = link.querySelector(".fw-icon-badge__count")
    if (count > 0) {
      if (!badge) {
        badge = document.createElement("span")
        badge.className = "fw-icon-badge__count"
        link.appendChild(badge)
      }
      badge.textContent = `${count}`
    } else if (badge) {
      badge.remove()
    }
  }

  svgImgTag(url, alt) {
    return `<img src="${url}" alt="${this.escapeHtml(alt)}" style="width: 50%;" />`
  }

  escapeHtml(str) {
    return `${str}`
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }

  escapeAttr(str) {
    return this.escapeHtml(str).replaceAll("`", "&#096;")
  }
}
